import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { LinkTabs } from "@/components/ui/link-tabs";
import { PageHeader } from "@/components/ui/page-header";
import { useApp } from "@/hooks/use-app-context";

/** Imports: one menu entry, one tab per kind of file. */
export function UploadsTabs({ className }: { className?: string }) {
  const { lang = "fr" } = useParams<{ lang: string }>();
  const { t } = useApp();
  const nav = t.admin.layout.nav;
  const base = `/${lang}/admin/uploads`;
  return (
    <LinkTabs
      className={className}
      label={nav.uploads}
      tabs={[
        { key: "audio", label: nav.audio, href: `${base}/audio`, icon: "volume2" },
        { key: "text", label: nav.text, href: `${base}/text`, icon: "fileText" },
        { key: "image", label: nav.images, href: `${base}/image`, icon: "image" },
        { key: "files", label: nav.importedFiles, href: `${base}/files`, icon: "download" },
      ]}
    />
  );
}

/** AI models: catalogue, endpoints and the testing bench. */
export function ModelsTabs({ className }: { className?: string }) {
  const { lang = "fr" } = useParams<{ lang: string }>();
  const { t } = useApp();
  const en = lang === "en";
  const base = `/${lang}/admin/config`;
  return (
    <LinkTabs
      className={className}
      label={en ? "AI models" : "Modèles IA"}
      tabs={[
        { key: "models", label: t.admin.layout.nav.models, href: `${base}/models`, icon: "zap" },
        { key: "api", label: t.admin.layout.nav.api, href: `${base}/api`, icon: "settings" },
        { key: "testing", label: en ? "Model testing" : "Tester les modèles", href: `${base}/model-performance`, icon: "barChart3" },
      ]}
    />
  );
}

interface TabbedHeaderProps {
  /** What the current tab is for. */
  description?: ReactNode;
  /** The current tab's own buttons. */
  actions?: ReactNode;
}

/**
 * The one title of the AI models entry, shared by its three tabs: the page
 * title (the menu label) with the tabs right under it.
 */
export function ModelsPageHeader({ description, actions }: TabbedHeaderProps = {}) {
  const { lang = "fr" } = useParams<{ lang: string }>();
  return (
    <>
      <PageHeader title={lang === "en" ? "AI models" : "Modèles IA"} description={description} actions={actions} className="border-b-0 pb-0" />
      <ModelsTabs />
    </>
  );
}

/** Same for Imports: the menu label as title, one tab per kind of file. */
export function UploadsPageHeader({ description, actions }: TabbedHeaderProps = {}) {
  const { t } = useApp();
  return (
    <>
      <PageHeader title={t.admin.layout.nav.uploads} description={description} actions={actions} className="border-b-0 pb-0" />
      <UploadsTabs />
    </>
  );
}
